// @ts-check
// Atualização automática das telas quando outra pessoa altera registros (contrato 08-contrato-navegacao-mobile.md,
// seção 6): uma consulta leve ao feed de mudanças a cada 15 segundos com a aba visível, parada com a aba oculta e feita
// na hora ao voltar para ela. Registros abertos são relidos e trocados no lugar (sem esqueleto); registros novos fazem a
// lista recarregar a primeira página em silêncio; contagens, agregações e timeline seguem o mutationCounterState.
import { signal } from '@preact/signals';
import { api } from './api-client.js';
import { currentWorkspaceMemberState, objectsByNameSingular } from './metadata-store.js';
import { findManyRecords } from './rest.js';
import { applyRemoteRecords, mutationCounterState, readLoadedRecordIds, remoteListChangeState } from './record-store.js';
import { findVanishedIds, groupRemoteChanges } from '../lib/remote-changes.js';
import { hasStageScope } from './stage-scope.js';

export const LIVE_UPDATE_INTERVAL_MILLISECONDS = 15000;
// ao voltar para a aba, no máximo uma consulta a cada 5 segundos
const MINIMUM_GAP_MILLISECONDS = 5000;
const MAXIMUM_IDS_PER_FETCH = 100;

/**
 * Último lote aplicado: a página do registro aberto confere se o registro dela mudou (chaves "objeto:id").
 * @type {import('@preact/signals').Signal<{ sequence: number, keys: Set<string>, isReset: boolean }>}
 */
export const remoteRecordChangeState = signal({ sequence: 0, keys: new Set(), isReset: false });

/** @typedef {import('../lib/remote-changes.js').RemoteChange} RemoteChange */

/** @param {RemoteChange[]} changes @param {boolean} isReset */
async function applyRemoteChanges(changes, isReset) {
  const ownMemberId = currentWorkspaceMemberState.value?.id ?? null;
  const grouped = groupRemoteChanges(changes, ownMemberId);
  if (!isReset && grouped.size === 0) return;
  const listBumps = { ...remoteListChangeState.value };
  /** @type {Set<string>} */
  const keys = new Set();
  for (const [objectNameSingular, entry] of grouped) {
    const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
    if (!objectMetadata) continue;
    for (const id of [...entry.updatedIds, ...entry.removedIds]) keys.add(`${objectNameSingular}:${id}`);
    const loaded = readLoadedRecordIds(objectNameSingular);
    const idsToFetch = [...entry.updatedIds].filter((id) => loaded.has(id)).slice(0, MAXIMUM_IDS_PER_FETCH);
    let freshRecords = [];
    /** @type {string[]} */
    let vanishedIds = [];
    if (idsToFetch.length > 0) {
      try {
        freshRecords = (await findManyRecords(objectMetadata, { filter: { field: 'id', operator: 'in', value: idsToFetch }, limit: idsToFetch.length, depth: 1 })).records;
        // papel restrito por etapa: o id que não volta foi para uma etapa que a pessoa não vê
        if (hasStageScope()) vanishedIds = findVanishedIds(idsToFetch, freshRecords);
      } catch {
        // a próxima consulta tenta de novo pela recarga da lista
      }
    }
    applyRemoteRecords(objectNameSingular, freshRecords, [...entry.removedIds, ...vanishedIds]);
    if (entry.hasNewRecords || [...entry.updatedIds].some((id) => !loaded.has(id))) listBumps[objectNameSingular] = (listBumps[objectNameSingular] ?? 0) + 1;
  }
  if (isReset) {
    // o servidor reiniciou ou ficou para trás: todas as listas abertas recarregam
    for (const objectNameSingular of objectsByNameSingular.value.keys()) listBumps[objectNameSingular] = (listBumps[objectNameSingular] ?? 0) + 1;
  }
  remoteListChangeState.value = listBumps;
  remoteRecordChangeState.value = { sequence: remoteRecordChangeState.value.sequence + 1, keys, isReset };
  // contagens, agregações, kanban e timeline releem pelo contador de escritas
  mutationCounterState.value += 1;
}

/** @type {string | null} */
let cursor = null;
let lastPollAt = 0;
let isPolling = false;
/** @type {ReturnType<typeof setInterval> | null} */
let timer = null;

export async function pollRecordChanges() {
  if (isPolling) return;
  isPolling = true;
  lastPollAt = Date.now();
  try {
    const body = await api.get('/rest/record-changes', cursor ? { cursor } : undefined, { skipUnauthorizedRedirect: true });
    const page = body?.data;
    if (!page?.cursor) return;
    const isFirstPoll = cursor === null;
    cursor = page.cursor;
    if (isFirstPoll) return;
    await applyRemoteChanges(Array.isArray(page.changes) ? page.changes : [], Boolean(page.isReset));
  } catch {
    // sem rede ou sessão: a tela segue como está e a próxima consulta tenta de novo
  } finally {
    isPolling = false;
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible' && Date.now() - lastPollAt >= MINIMUM_GAP_MILLISECONDS) pollRecordChanges();
}

/** Liga a consulta periódica (uma por aba); devolve a função que desliga. */
export function startLiveUpdates() {
  if (timer) return stopLiveUpdates;
  pollRecordChanges();
  timer = setInterval(() => {
    // aba oculta não consulta: o Durable Object só trabalha para quem está olhando
    if (document.visibilityState === 'visible') pollRecordChanges();
  }, LIVE_UPDATE_INTERVAL_MILLISECONDS);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  return stopLiveUpdates;
}

export function stopLiveUpdates() {
  if (timer) clearInterval(timer);
  timer = null;
  cursor = null;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
}
